// Suppliers — the list Material Inward picks from (the same AccountsVendor
// master Accounts bills against). Server: src/api-inventory/suppliers.js.
import { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Search, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useUser } from '@/shared/hooks/useAuth';
import { useSuppliers, useSaveSupplier, materialErrorText, type Supplier } from '../hooks/useMaterials';
import { AddSupplierModal, Panel, Pill } from './materialShared';

const isApprover = (role?: string) => ['superadmin', 'owner', 'admin'].includes(String(role || ''));

export function SuppliersTab() {
  const user = useUser();
  const admin = isApprover(user?.role);
  const { data: suppliers = [], isLoading } = useSuppliers(admin);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? suppliers.filter((x) => [x.name, ...x.aliases, x.city, x.gstNumber].some((v) => v.toLowerCase().includes(s))) : suppliers;
  }, [suppliers, q]);

  return (
    <Panel
      title={`Suppliers (${suppliers.filter((s) => s.isActive).length})`}
      subtitle="One entry per supplier. Other spellings used on bills are kept, so searching any of them finds the supplier."
      actions={<>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, other spelling, city…" className="h-9 w-60 rounded-lg border border-input bg-surface pl-8 pr-3 text-body-sm outline-none focus:border-primary" />
        </div>
        <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> Add supplier</Button>
      </>}
    >
      {isLoading ? <div className="p-5"><Skeleton className="h-40" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-body-sm">
            <thead className="border-b border-border bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
              <tr>
                <th className="px-5 py-2.5">Supplier</th>
                <th className="px-3 py-2.5">Other spellings</th>
                <th className="px-3 py-2.5">Phone</th>
                <th className="px-3 py-2.5">City</th>
                <th className="px-3 py-2.5">GSTIN</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {shown.map((s) => (
                <tr key={s._id} className={s.isActive ? 'hover:bg-surface-container-low/60' : 'opacity-55'}>
                  <td className="px-5 py-2.5 font-medium text-on-surface">{s.name}{!s.isActive && <Pill className="ml-2">inactive</Pill>}</td>
                  <td className="max-w-[320px] px-3 py-2.5 text-on-surface-variant">
                    <span className="line-clamp-2" title={s.aliases.join(', ')}>{s.aliases.join(', ') || '—'}</span>
                  </td>
                  <td className="px-3 py-2.5 text-on-surface-variant">{s.phone || '—'}</td>
                  <td className="px-3 py-2.5 text-on-surface-variant">{s.city || '—'}</td>
                  <td className="px-3 py-2.5 font-mono text-on-surface-variant">{s.gstNumber || '—'}</td>
                  <td className="px-5 py-2.5 text-right">
                    {admin && <Button size="sm" variant="ghost" onClick={() => setEditing(s)} aria-label={`Edit ${s.name}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!shown.length && (
            <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
              <Truck className="h-8 w-8 text-on-surface-subtle" aria-hidden />
              <p className="text-body-md font-medium text-on-surface">{q ? 'No supplier matches' : 'No suppliers yet'}</p>
            </div>
          )}
        </div>
      )}
      <AddSupplierModal open={adding} onClose={() => setAdding(false)} onAdded={() => setAdding(false)} />
      <EditSupplierModal supplier={editing} onClose={() => setEditing(null)} />
    </Panel>
  );
}

function EditSupplierModal({ supplier, onClose }: { supplier: Supplier | null; onClose: () => void }) {
  const save = useSaveSupplier();
  const [f, setF] = useState({ name: '', phone: '', city: '', gstNumber: '', aliases: '', isActive: true });
  useEffect(() => {
    if (supplier) setF({ name: supplier.name, phone: supplier.phone, city: supplier.city, gstNumber: supplier.gstNumber, aliases: supplier.aliases.join('\n'), isActive: supplier.isActive });
  }, [supplier]);
  if (!supplier) return null;
  const submit = async () => {
    try {
      await save.mutateAsync({ id: supplier._id, ...f, aliases: f.aliases.split('\n').map((a) => a.trim()).filter(Boolean) });
      toast.success('Supplier saved');
      onClose();
    } catch (err) {
      toast.error(materialErrorText(err, 'Could not save.'));
    }
  };
  return (
    <Modal open onClose={onClose} size="md" title={`Edit ${supplier.name}`} footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={save.isPending}>Save</Button></>
    )}>
      <div className="space-y-3">
        <Input label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <Input label="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
        </div>
        <Input label="GSTIN" value={f.gstNumber} maxLength={15} onChange={(e) => setF({ ...f, gstNumber: e.target.value.toUpperCase() })} />
        <label className="block">
          <span className="mb-1 block text-body-sm font-medium text-on-surface">Other spellings (one per line)</span>
          <textarea value={f.aliases} onChange={(e) => setF({ ...f, aliases: e.target.value })} rows={4} className="w-full rounded-lg border border-input bg-surface px-3 py-2 font-mono text-body-sm outline-none focus:border-primary" />
        </label>
        <label className="flex items-center gap-2 text-body-sm text-on-surface">
          <input type="checkbox" checked={f.isActive} onChange={(e) => setF({ ...f, isActive: e.target.checked })} className="h-4 w-4 accent-primary" /> Active (shown on the inward form)
        </label>
      </div>
    </Modal>
  );
}

export default SuppliersTab;
